import React from 'react';
import { styled, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useLaptop } from 'hooks';
import { GetStaticProps } from 'next';
import { getTranslations } from 'utils/i18n-cache';

const TermsOfUsePage = () => {
  const { isOverLaptop } = useLaptop();

  return (
    <Box>
      <Content>
        {renderTitle('Terms of Use')}
        {renderText(`
          These Terms of Use ("Terms") govern your access to and use of our
          website, located at https://shifa.one (the "Website"). By using
          the Website, you agree to comply with these Terms. If you do not agree
          with any provision of these Terms, please do not use the Website.
        `)}
        <br />

        {renderSubtitle('Acceptance of Terms')}
        {renderText(`
          By accessing or using the Website, you acknowledge that you have read,
          understood, and agree to be bound by these Terms, as well as any
          additional terms and conditions referenced here in.
        `)}
        <br />

        {renderSubtitle('Services and Products')}
        {renderText(`
          a. For general inquiries, questions, or information related to Shifa One, please contact us at info@shifa.one.
        `)}
        {renderText(`
          b. For support or assistance with any issues or problems encountered on the Website, please contact us at support@shifa.one.
        `)}
        <br />

        {renderSubtitle('User Accounts')}
        {renderText(`
          a. In order to access certain features or make purchases on the Website, you may be required to create a user account. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account.
        `)}
        {renderText(`
          b. You agree to provide accurate, current, and complete information during the registration process. You must promptly update any changes to your account information to ensure its accuracy.
        `)}
        <br />

        {renderSubtitle('Contact Information')}
        {renderText(`
          a. Shifa One operates as an online platform that sells gift cards and vouchers for various telecom services, access to streaming services, game gift cards, and more.
        `)}
        {renderText(`
          b. The Website is owned and operated by Prepaid Solution FZCO ("Website Owner").
        `)}
        <br />

        {renderSubtitle('Refunds')}
        {renderText(`
          a. Please note that displayed vouchers and gift cards purchased through the Website are not refundable unless otherwise specified. All sales are final.
        `)}
        <br />

        {renderSubtitle('Intellectual Property')}
        {renderText(`
          a. All trademarks and brand names displayed on the Website belong to their respective owners. The inclusion of these trademarks does not imply any endorsement or affiliation with Shifa One.
        `)}
        {renderText(`
          b. You may not use any of the trademarks or brand names displayed on the Website without the explicit permission of the respective owners.
        `)}
        <br />

        {renderSubtitle('User Conduct')}
        {renderText(`
          a. You agree to use the Website in accordance with applicable laws and regulations. You shall not engage in any activity that:
        `)}
        <br />
        {renderUl([
          'Violates any laws or regulations;',
          'Infringes upon the rights of others;',
          'Harasses, threatens, or causes distress to others;',
          'Uploads or transmits any harmful or malicious content;',
          'Interferes with the operation of the Website;',
          'Attempts to gain unauthorized access to any part of the Website;',
          'Engages in any fraudulent activity.',
        ])}
        <br />

        {renderSubtitle('Limitations on Use')}
        {renderText(`
          a. Some gift cards or vouchers available on the Website may have territorial limitations or restrictions. It is your responsibility to review and comply with any such limitations or restrictions before making a purchase.
        `)}
        <br />

        {renderSubtitle('Termination')}
        {renderText(`
          a. We reserve the right to terminate or suspend your access to the Website, without prior notice or liability, for any reason whatsoever, including but not limited to a breach of these Terms.
        `)}
        <br />

        {renderSubtitle('Disclaimer of Warranties')}
        {renderText(`
          a. The Website and its content are provided on an "as is" and "as available" basis. While we strive to provide accurate and up-to-date information, we do not make any warranties or representations of any kind, express or implied, regarding the Website's accuracy, reliability, or availability.
        `)}
        {renderText(`
          b. You acknowledge and agree that your use of the Website is at your own risk. Shifa One and the Website Owner disclaim all warranties, whether express or implied, including but not limited to the warranties of merchantability, fitness for a particular purpose, and non-infringement.
        `)}
        <br />

        {renderSubtitle('Limitation of Liability')}
        {renderText(`
          a. To the maximum extent permitted by applicable law, Shifa One and the Website Owner shall not be liable for any direct, indirect, incidental, special, consequential, or exemplary damages, including but not limited to damages for loss of profits, goodwill, use, data, or other intangible losses, resulting from:
        `)}
        <br />
        {renderUl([
          'The use or inability to use the Website;',
          'Unauthorized access to or alteration of your transmissions or data;',
          'Any conduct or content of any third party on the Website;',
          'Any content obtained from the Website; or',
          'Any other matter relating to the Website.',
        ])}
        <br />

        {renderSubtitle('Modifications to the Terms')}
        {renderText(`
          a. Shifa One reserves the right to modify or update these Terms at any time without prior notice. It is your responsibility to review these Terms periodically for any changes. Your continued use of the Website after any modifications to these Terms constitutes acceptance of those changes.
        `)}
        <br />

        {renderSubtitle('Governing Law')}
        {renderText(`
          a. These Terms shall be governed by and construed in accordance with the laws of the jurisdiction where the Website Owner is based, without regard to its conflict of law provisions.
        `)}
        <br />

        {renderSubtitle('Severability')}
        {renderText(`
          a. If any provision of these Terms is found to be invalid or unenforceable, that provision shall be deemed severable and shall not affect the validity and enforceability of the remaining provisions.
        `)}
      </Content>
    </Box>
  );

  function renderTitle(text: string) {
    return (
      <Typography
        variant={isOverLaptop ? 'big500' : 'big700'}
        component='h1'
        mb={isOverLaptop ? 3 : 1.5}
      >
        {text}
      </Typography>
    );
  }

  function renderSubtitle(text: string) {
    return (
      <Typography variant={isOverLaptop ? 'h6' : 'h5'} component='h2' mb={1}>
        {text}
      </Typography>
    );
  }

  function renderText(text: string) {
    return (
      <Typography
        component='p'
        variant={isOverLaptop ? 'average400' : 'average200'}
      >
        {text}
      </Typography>
    );
  }

  function renderUl(texts: string[]) {
    return (
      <Typography
        component='ul'
        variant={isOverLaptop ? 'average400' : 'average200'}
        pl={0}
        sx={{ listStyle: 'inside' }}
      >
        {texts.map((text, index) => (
          <li key={index}>{text}</li>
        ))}
      </Typography>
    );
  }
};

export default TermsOfUsePage;

export const Content = styled(Box)(({ theme }) => ({
  padding: '60px 0 80px 0',
  [theme.breakpoints.down('laptop')]: {
    padding: '24px 0 40px 0',
  },
}));

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await getTranslations(locale ?? 'en')),
  },
});
